import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DocumentPreviewModal = ({ isOpen, onClose, document, type }) => {
  if (!isOpen || !document) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = () => {
    // Mock email functionality
    alert(`${type === 'invoice' ? 'Invoice' : 'Challan'} sent to client email`);
  };

  const handleDownload = () => {
    // Mock download functionality
    alert(`${type === 'invoice' ? 'Invoice' : 'Challan'} downloaded as PDF`);
  };

  return (
  <div className="fixed inset-0 bg-black/50 z-[1001] flex items-center justify-center p-2 sm:p-4">
    <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
      
      {/* Modal Header */}
      <div className="flex flex-wrap sm:flex-nowrap items-center justify-between p-4 sm:p-6 border-b border-border gap-3">
        <div className="flex items-center space-x-3">
          <Icon name={type === 'invoice' ? 'FileText' : 'Truck'} size={24} className="text-primary" />
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">
              {type === 'invoice' ? 'Invoice Preview' : 'Challan Preview'}
            </h2>
            <p className="text-sm text-muted-foreground break-all">{document.id}</p>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} iconName="Printer" iconPosition="left">Print</Button>
          <Button variant="outline" size="sm" onClick={handleEmail} iconName="Mail" iconPosition="left">Email</Button>
          <Button variant="outline" size="sm" onClick={handleDownload} iconName="Download" iconPosition="left">Download</Button>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={20} />
        </div>
      </div>

      {/* Modal Content */}
      <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-112px)] text-sm sm:text-base">
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-8 max-w-full mx-auto text-gray-800">
          
          {/* Company Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-lg sm:text-2xl font-bold">NijanandTextile</h1>
            <p className="text-sm text-gray-600">Textile Manufacturing & Trading</p>
            <p className="text-xs sm:text-sm text-gray-500 whitespace-pre-line">
              123 Industrial Area, Textile Hub, Mumbai - 400001{'\n'}
              Phone: +91 98765 43210 | Email: info@nijanandtextile.com{'\n'}
              GST: 27ABCDE1234F1Z5
            </p>
          </div>

          {/* Document Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8">
            <div>
              <h2 className="text-lg sm:text-xl font-bold mb-2">{type === 'invoice' ? 'INVOICE' : 'DELIVERY CHALLAN'}</h2>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>{type === 'invoice' ? 'Invoice' : 'Challan'} No:</strong> {document.id}</p>
                <p><strong>Date:</strong> {formatDate(type === 'invoice' ? document.issueDate : document.dispatchDate)}</p>
                {type === 'invoice' && <p><strong>Due Date:</strong> {formatDate(document.dueDate)}</p>}
                {type === 'challan' && document.trackingNumber && (
                  <p><strong>Tracking No:</strong> {document.trackingNumber}</p>
                )}
              </div>
            </div>
            <div className="text-sm text-gray-600 text-left sm:text-right">
              <h3 className="font-semibold text-gray-900 mb-1">Bill To:</h3>
              <p className="font-medium">{document.client}</p>
              <p>Client Address Line 1</p>
              <p>Client Address Line 2</p>
              <p>City, State - 400001</p>
              <p>GST: 27ABCDE1234F1Z5</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-6 sm:mb-8 overflow-x-auto">
            <table className="w-full border border-gray-300 text-xs sm:text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border px-3 py-2 text-left font-medium text-gray-900">Sr. No.</th>
                  <th className="border px-3 py-2 text-left font-medium text-gray-900">Design/Item</th>
                  <th className="border px-3 py-2 text-center font-medium text-gray-900">Quantity</th>
                  {type === 'invoice' && (
                    <>
                      <th className="border px-3 py-2 text-right font-medium text-gray-900">Rate</th>
                      <th className="border px-3 py-2 text-right font-medium text-gray-900">Amount</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {document.designs?.map((design, index) => (
                  <tr key={index}>
                    <td className="border px-3 py-2 text-gray-900">{index + 1}</td>
                    <td className="border px-3 py-2">{design}</td>
                    <td className="border px-3 py-2 text-center">
                      {Math.floor(document.items / document.designs.length)} pcs
                    </td>
                    {type === 'invoice' && (
                      <>
                        <td className="border px-3 py-2 text-right">₹{(Math.floor(Math.random() * 500) + 100).toLocaleString('en-IN')}</td>
                        <td className="border px-3 py-2 text-right">₹{(Math.floor(Math.random() * 50000) + 10000).toLocaleString('en-IN')}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Invoice Totals */}
          {type === 'invoice' && (
            <div className="flex justify-end mb-6">
              <div className="w-full max-w-xs space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal:</span><span>₹{(parseInt(document.amount.replace(/[₹,]/g, '')) * 0.85).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span>CGST (9%):</span><span>₹{(parseInt(document.amount.replace(/[₹,]/g, '')) * 0.075).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span>SGST (9%):</span><span>₹{(parseInt(document.amount.replace(/[₹,]/g, '')) * 0.075).toLocaleString('en-IN')}</span></div>
                <div className="border-t pt-2 border-gray-300 flex justify-between font-semibold"><span>Total Amount:</span><span>{document.amount}</span></div>
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">Terms & Conditions:</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <p>1. Payment terms: {document.clientType === 'online' ? '15 days' : '30 days'} from invoice date</p>
              <p>2. Goods once sold will not be taken back except for manufacturing defects</p>
              <p>3. Any disputes subject to Mumbai jurisdiction only</p>
              <p>4. Interest @ 18% per annum will be charged on overdue amounts</p>
            </div>
          </div>

          {/* Signatures */}
          <div className="flex flex-col sm:flex-row justify-between items-end gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-6">Receiver's Signature</p>
              <div className="border-t border-gray-400 w-32" />
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-6">For NijanandTextile</p>
              <div className="border-t border-gray-400 w-32" />
              <p className="text-xs text-gray-500 mt-1">Authorized Signatory</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
);

};

export default DocumentPreviewModal;